import React, { useState } from "react";
import { PageTitle } from "domain/feature/hub/pageTitle/styles";
import { trans } from "sharedKernel/i18n";
import { Row, Col, Button } from "react-bootstrap";
import { NoContent } from "components/NoContent";
import { Loading } from "components/loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import LearningBarsIcon from "assets/images/defaults/learning-bars.svg";
import { type MyLearningTrack, type TracksListResponse } from "data/services/trackService";
import { useMyLearningTracks } from "./hooks/useMyLearningTracks";
import { TrackContainer, type Track } from "domain/track/trackContainer";
import { trackService } from "data/services/trackService";
import { FilterOrderBar } from "sharedKernel/styledComponents/filterComponents/style";
import { Filter } from "components/Filter";
import { FilterOrderByProps, OrderBy } from "components/OrderBy";

export const LearningPage = ({ ...props }): JSX.Element => {
  const navigate = useNavigate();
  
  // Estados para busca e ordenação
  const [selectedOrderBy, setSelectedOrderBy] = useState<FilterOrderByProps>({
    value: "title asc",
    label: trans("Ordem alfabética"),
  });
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  
  const { 
    data,
    tracks, 
    isLoading, 
    error, 
    hasTracks,
    applyFilters
  } = useMyLearningTracks();

  // Opções de ordenação
  const orderByOptions = [
    {
      value: "title asc",
      label: trans("Ordem alfabética"),
    },
    {
      value: "updated_at desc",
      label: trans("Mais recente"),
    },
  ];

  const labels = {
    title: trans("Meus aprendizados"),
    noContent: trans("Você ainda não começou nenhuma trilha"),
  };

  // Funções de busca e ordenação
  const onOrderBy = (selectedOrderBy: FilterOrderByProps): void => {
    setSelectedOrderBy(selectedOrderBy);
    applyFilters(currentSearchTerm, selectedOrderBy.value);
  };

  const onSearch = (searchTerm: string): void => {
    setCurrentSearchTerm(searchTerm);
    applyFilters(searchTerm, selectedOrderBy.value);
  };

  const FilterOrderBarElement = (): JSX.Element => {
    return (
      <FilterOrderBar>
        <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
        <OrderBy
          selectedValue={selectedOrderBy}
          onSelect={onOrderBy}
          options={orderByOptions}
        />
      </FilterOrderBar>
    );
  };

  const getTrackProgress = (track: any) => {
    const progress = Number(track.userProgress ?? track.progress ?? 0);
    const completed = Number(track.userCompletedLessons ?? track.completedLessons ?? 0);
    return { 
      completedLessons: isNaN(completed) ? 0 : completed, 
      progressPercentage: isNaN(progress) ? 0 : Math.round(progress) 
    };
  };

  const convertedTracks: Track[] = tracks.map(track => {
    const { completedLessons, progressPercentage } = getTrackProgress(track);
    const isCompleted = progressPercentage >= 100;
    
    return {
      id: track.id,
      title: track.title,
      description: track.description,
      imageUrl: track.imageUrl,
      status: track.status as any,
      numberOfLessons: track.numberOfLessons || 0,
      totalOfMinutes: track.totalOfMinutes || 0,
      createdAt: track.createdAt,
      completedLessons,
      progressPercentage,
      isCompleted
    };
  });


  const openTrack = async (track: Track) => {
    try {
      const trackDetails = await trackService.getTrackDetails(String(track.id));
      
      if (trackDetails.lessons && trackDetails.lessons.length > 0) {
        const firstLesson = trackDetails.lessons[0];
        navigate(`/hub/learning/lesson/${firstLesson.id}`);
      } else {
        navigate(`/hub/available-tracks/details/${track.id}`, { state: { track } });
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da trilha:', error);
      navigate(`/hub/available-tracks/details/${track.id}`, { state: { track } });
    }
  };

  if (isLoading) {
    return (
      <>
        <Row className="align-items-center">
          <Col>
            <PageTitle>{labels.title}</PageTitle>
          </Col>
        </Row>
        <Loading />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Row className="align-items-center">
          <Col>
            <PageTitle>{labels.title}</PageTitle>
          </Col>
        </Row>
        <div className="text-center py-5">
          <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} className="me-2 text-warning" />
          <p className="text-danger">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Row className="align-items-center mb-4">
        <Col>
          <PageTitle>{labels.title}</PageTitle>
        </Col>
      </Row>
      
      {hasTracks && (
        <Row>
          <FilterOrderBarElement />
        </Row>
      )}
      
      {!hasTracks ? (
        <NoContent 
          text={labels.noContent} 
          image={LearningBarsIcon} 
          imageWidth="250px" 
          imageHeight="250px" 
          disableImageFilter={true}
        />
      ) : (
        <TrackContainer
          tracks={convertedTracks}
          getInfoItems={(t: any) => [
            {
              icon: (
                <FontAwesomeIcon className="fa-icon" icon={["fas", "chart-simple"]} />
              ),
              text: `${t.progressPercentage}% concluído • ${t.completedLessons}/${t.numberOfLessons} aulas`,
            },
          ]}
          getActions={(t: any) => [
            {
              label: t.isCompleted ? "Começar Novamente" : "Continuar",
              kind: "primary",
              onClick: () => openTrack(t),
            },
          ]}
          onCardClick={openTrack}
        />
      )}
    </>
  );
};
